import { useMutation } from '@apollo/client';
import { ZonedDateTime } from '@js-joda/core';
import { Box, Button, IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import React, { useCallback, useMemo, useState } from 'react';
import { compose, withProps } from 'recompose';
import { setPatientEventTimestamp } from '../../../../graph/procedureSteps';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import { withLabel } from '@/se/components/Label';
import { RouterlessModal } from '@/se/components/Modal';
import { CancelableForm, DateInput, formatTime } from '../../../inputs/timeEdit/TimeEditForm';
import DateDropdownInput from '@/se/components/inputs/DateInput';
import { makeStyles } from '@material-ui/core/styles';

export const convertToDateTime = (timestamp, time, date) => {
  const dateTime = new Date(timestamp);
  if (date) {
    const [year, month, day] = date.split('-').map(e => parseInt(e, 10));
    if (isFinite(year) && isFinite(month) && isFinite(day)) {
      dateTime.setFullYear(year, month - 1, day);
    }
  }
  const [hours, minutes] = time.split(':').map(e => parseInt(e, 10));
  dateTime.setHours(hours);
  dateTime.setMinutes(minutes);
  return dateTime;
};

const getEventType = event => {
  switch (event?.type) {
    case 'Created':
    case 'Discharged':
    case 'BlockNerve':
    case 'CleaningCompleted':
    case 'OrRoomReady':
      return { __type: `${event?.type}$` };
    case 'TransferredToChair':
    case 'TransferredToPT':
    case 'PTComplete':
      return { __type: `${event?.type}`, manualEntryBy: null, manualTimestamp: null };
    case 'Entered':
      return { __type: 'Entered', roomId: event?.room?.id, manualEntryBy: null, manualTimestamp: null };
    case 'Exited':
      return { __type: 'Exited', roomId: event?.room?.id, manualEntryBy: null };
    case 'ProcedureStatusSet':
      return {
        __type: 'ProcedureStatusSet',
        status: { [event?.status]: {} },
        manualEntryBy: null,
        manualTimestamp: null,
      };
    default:
      return null;
  }
};

export const FormInput = (eventText, withDate = false, options: any = {}) =>
  withProps({
    schema: {
      ...(withDate && {
        date: compose(
          withLabel('Date'),
          withProps({
            defaultDate: options.defaultDate,
          })
        )(DateDropdownInput),
      }),
      time: compose(
        withLabel(eventText),
        withProps({
          autoFocus: true,
          required: true,
        })
      )(DateInput),
    },
  })(ObjectInput);

const PatientEventEditButton = ({ patientId, event, eventText }) => {
  const [edit, setEdit] = useState(false);
  const [save] = useMutation(setPatientEventTimestamp);
  const classes = useStyles();
  const includeDate = event?.type === 'Discharged';

  const toggleEdit = useCallback(() => {
    setEdit(prev => !prev);
  }, []);

  const saveTimestamp = useCallback(
    async (time, date) => {
      if (!time) {
        toggleEdit();
        return;
      }
      const eventType = getEventType(event);
      const newDateTime = convertToDateTime(event?.timestamp, time, date);
      newDateTime.toISOString() !== new Date(event?.timestamp).toISOString() &&
        patientId &&
        eventType &&
        (await save({
          variables: {
            patientId,
            eventType: JSON.stringify(eventType),
            timestamp: newDateTime,
          },
        }));
      toggleEdit();
    },
    [event, patientId, save, toggleEdit]
  );

  const onSubmit = useCallback(
    async val => {
      await saveTimestamp(val?.time, val?.date);
    },
    [saveTimestamp]
  );

  const eventZdt = useMemo(
    () => (event?.timestamp ? ZonedDateTime.parse(event.timestamp) : undefined),
    [event?.timestamp]
  );
  const initialTime = useMemo(
    () => (eventZdt ? formatTime(eventZdt.toLocalDateTime().toString()) : undefined),
    [eventZdt]
  );
  const initialDate = useMemo(() => (eventZdt ? eventZdt.toLocalDate().toString() : undefined), [eventZdt]);
  const defaultDate = useMemo(() => (event?.timestamp ? new Date(event.timestamp) : undefined), [event?.timestamp]);
  const inputComponent = useMemo(
    () => FormInput(eventText, includeDate, { defaultDate }),
    [eventText, includeDate, defaultDate]
  );

  return (
    <>
      <Box>
        <IconButton color="primary" onClick={toggleEdit} className={classes.mobileButton}>
          <EditIcon />
        </IconButton>
      </Box>
      <Button
        variant="outlined"
        size="small"
        onClick={toggleEdit}
        startIcon={<EditIcon />}
        className={classes.desktopButton}
      >
        Edit
      </Button>
      {edit && (
        <RouterlessModal title="Update Time" onClose={toggleEdit}>
          <CancelableForm
            autoFocus
            label="Update"
            onClose={toggleEdit}
            initialValue={{
              time: initialTime,
              ...(includeDate ? { date: initialDate } : {}),
            }}
            input={inputComponent}
            onSubmit={onSubmit}
            busy={false}
          />
        </RouterlessModal>
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  mobileButton: {
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  desktopButton: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

export default PatientEventEditButton;
